import { compareSortText } from '../dashboard/useTableSort';

/**
 * Who stands in a store, as the Stores table's "Deployed Employee" and "Reporting Manager"
 * columns, its search box and its download read them.
 *
 * Everything comes from the `employees` GET /stores already sends with each store (the
 * store_employees rows, each with its reporting manager from one batched lookup), so the
 * columns cost no request of their own. Worked out once per store when the list arrives,
 * not per render: the page lists ~2,600 stores and re-renders on every keystroke in its forms.
 */

/** One person on a store as GET /stores sends them: the fields these columns read. */
export interface StorePersonSource {
  employee_code?: string | null;
  employee_name?: string | null;
  reporting_manager_name?: string | null;
  reporting_manager_code?: string | null;
  is_primary?: boolean | null;
  /** false = they have left 1Office and the mapping is a leftover; not shown. */
  is_active?: boolean | null;
}

/** A deployed person as the two columns show them. Each value trimmed, '' when not known. */
export interface StorePerson {
  name: string;
  code: string;
  managerName: string;
  managerCode: string;
}

/** A store with nobody on it: one shared empty list. */
export const NO_STORE_PEOPLE: readonly StorePerson[] = [];

/** The dash a cell, or a missing value in a download cell, reads when there is nothing to show. */
export const NO_VALUE = '—';

/** What a download cell joins several people's values with. */
export const PEOPLE_JOINER = '; ';

const clean = (value: unknown): string => (value == null ? '' : String(value).trim());

/**
 * What a person's line leads with: their name, or their code when the name is not known.
 * The same rule for the manager: name, else code. '' when neither is known.
 */
export const personHeadline = (name: string, code: string): string => name || code;

/**
 * The people a store's columns list: the ACTIVE ones (an explicit `is_active: false` is a
 * leftover mapping of someone who has left; a row without the flag counts as active, as
 * everywhere else on this page), the primary employee first, then by name A to Z. People
 * whose names compare equal keep the order the server sent them in.
 */
export const storePeopleOf = (
  employees: readonly StorePersonSource[] | null | undefined,
): readonly StorePerson[] => {
  if (!Array.isArray(employees) || employees.length === 0) return NO_STORE_PEOPLE;
  const rows = employees
    .filter((e) => e != null && e.is_active !== false)
    .map((e) => ({
      primary: e.is_primary === true,
      person: {
        name: clean(e.employee_name),
        code: clean(e.employee_code),
        managerName: clean(e.reporting_manager_name),
        managerCode: clean(e.reporting_manager_code),
      },
    }));
  if (rows.length === 0) return NO_STORE_PEOPLE;
  rows.sort((a, b) => {
    if (a.primary !== b.primary) return a.primary ? -1 : 1;
    return compareSortText(personHeadline(a.person.name, a.person.code), personHeadline(b.person.name, b.person.code));
  });
  return rows.map((r) => r.person);
};

/**
 * Every value the search box matches a store's people on, lower-cased: each person's name
 * and code, and their manager's name and code. Blanks left out.
 */
export const storePeopleSearchTerms = (people: readonly StorePerson[]): readonly string[] => {
  const terms: string[] = [];
  for (const p of people) {
    for (const value of [p.name, p.code, p.managerName, p.managerCode]) {
      if (value) terms.push(value.toLowerCase());
    }
  }
  return terms;
};

/** The four download cells for a store, each person's value in the same position in each. */
export interface StorePeopleDownloadCells {
  employee: string;
  employeeCode: string;
  manager: string;
  managerCode: string;
}

/**
 * The store's people as the download's four cells: several people joined by "; " in the
 * order the table lists them, so the n-th name lines up with the n-th code and the n-th
 * manager. A value that is not known is written as a dash, so it still holds its place.
 * A store with nobody on it gives four blank cells.
 */
export const storePeopleDownloadCells = (people: readonly StorePerson[]): StorePeopleDownloadCells => {
  const join = (pick: (p: StorePerson) => string): string =>
    people.map((p) => pick(p) || NO_VALUE).join(PEOPLE_JOINER);
  return {
    employee: join((p) => p.name),
    employeeCode: join((p) => p.code),
    manager: join((p) => p.managerName),
    managerCode: join((p) => p.managerCode),
  };
};
